import React, { useCallback, useMemo } from 'react';
import { Dialog, DialogContent, Stack, Typography, useTheme } from "@mui/material";

import { useTranslations } from "@fenetech/translations";
import useIsMobile from "helpers/hooks/useIsMobile";

import { IContainerDetail } from "helpers/interfaces";
import useOrderData from "../useOrderData";
import CustomDataGridPro from "components/Common/CustomDataGridPro";
import { GridRowIdGetter, GridRowModel } from "@mui/x-data-grid-pro";
import DataGridColumnGenerator from "components/Common/DataGridColumnGenerator";
import Format from "helpers/fv.format";
import useFormatHelper from "helpers/hooks/useFormatHelper";

interface IProps {
    dialogVisible: boolean;
    onClose: () => void;
}

interface IContainerDetailRow extends IContainerDetail {
    orderItem: string,
    scheduleUnit: string,
    formattedPart: string,
    formattedSize: string,
}

const ContainerDetail: React.FC<IProps> = ({ dialogVisible, onClose }: IProps) => {

    const tm = useTranslations();
    const theme = useTheme();
    const isMobile = useIsMobile();
    const formatMethods = useFormatHelper();
    const { order, containerDetail } = useOrderData();

    const containerDetailRows = useMemo(() => {
        const details: IContainerDetailRow[] = [];

        if (order && containerDetail?.details) {
            containerDetail.details.forEach((cd) => {
                const props: IContainerDetailRow = {
                    ...cd,
                    orderItem: `${cd.orderNumber} - ${Format.FormatLineItem(cd.lineItem, cd.subLineItem)}`,
                    scheduleUnit: `${cd.schedID}-${cd.unitID}`,
                    formattedPart: `${Format.FormatPartDescription(cd.partNo, cd.partNoSuffix, cd.description)}`,
                    formattedSize: `${formatMethods.FormatDimensions(cd.width, cd.height, cd.thickness, order.engineeringUnitSetID)}`,
                };
                details.push(props);
            });

            return details;

        } else {
            return null;
        }

    }, [containerDetail?.details, order, formatMethods]);

    const getOrderUri = useCallback((row: IContainerDetailRow): string => {

        if (row.userCanAccessOrder) {
            return `/orders`;
        }

        return "";
    }, []);

    const getOrderParams = useCallback((row: IContainerDetailRow) => {
        if (row.userCanAccessOrder) {
            return {
                oKey: row.oKey
            }
        }
    }, []);

    const columns = useMemo(() => {
        const generator = new DataGridColumnGenerator(tm, containerDetailRows, theme, isMobile);
        generator.TryAddNumericColumn("slot", "Slot", undefined, {});
        generator.TryAddHyperlinkColumn("orderItem", "Order-Item", getOrderUri, getOrderParams, { hideable: false });
        generator.TryAddTextColumn("scheduleUnit", "Schedule-Unit", {});
        generator.TryAddNumericColumn("containerQty", "Quantity", undefined, {});
        generator.TryAddTextColumn("formattedPart", "Part", {});
        generator.TryAddTextColumn("formattedSize", "Size", {});
        return generator.GetColumns();
    }, [tm, containerDetailRows, theme, isMobile, getOrderUri, getOrderParams]);

    const getRowId: GridRowIdGetter = (row: GridRowModel) => {
        const container = row as IContainerDetail;
        return container.slot;
    };

    return <>
        <Dialog
            open={dialogVisible}
            onClose={onClose}
            fullWidth={true}
            maxWidth={"lg"}
        >
            <DialogContent sx={{ padding: 1 }}>
                <Stack direction="column" gap={1}>
                    <Typography variant="h5">
                        {tm.Get("Shipping Container Details")}
                    </Typography>
                    <Typography>
                        {tm.Get("Shipping Container:")} {containerDetail?.container.containerKey}
                    </Typography>
                    {containerDetail &&
                        <CustomDataGridPro
                            getRowId={getRowId}
                            columns={columns}
                            rows={containerDetailRows}
                            tableName="ContainerDetail"
                            tableSubName={isMobile ? "Mobile" : ""}
                            hideInMobileColumns={[
                                "scheduleUnit",
                                "containerQty"
                            ]}
                        />
                    }
                </Stack>
            </DialogContent>
        </Dialog>
    </>;

}

export default ContainerDetail;